/**
 * React Context Providers Directory
 *
 * This directory contains React context providers that wrap the application
 * to provide global state management and shared functionality across components.
 *
 * Current providers:
 * - QueryProvider: TanStack Query (React Query) provider for server state management
 */

"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";

/**
 * QueryProvider - TanStack Query (React Query) Context Provider
 *
 * Provides TanStack Query functionality throughout the application for efficient
 * server state management, data fetching, caching, and synchronization.
 *
 * Configuration:
 * - staleTime: 60 seconds - Data is considered fresh for 1 minute before refetching
 * - refetchOnWindowFocus: false - Prevents automatic refetch when window regains focus
 *
 * This provider is wrapped around the entire app in layout.tsx to enable
 * useQuery and useMutation hooks throughout the application.
 */
export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // Cache data for 60 seconds
            refetchOnWindowFocus: false, // Don't refetch on window focus
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}

